// De downloadpagina voor een externe ontvanger van "Bestanden delen" — /download/:token.
//
// Joris, 20 okt 2026: "Kan je met bestanden doorsturen ook doorsturen naar externen." De externe
// ontvanger krijgt een mail met een persoonlijke link hierheen: geen inlog, geen menu's, alleen dit
// bestand (of deze map). Rustig: bestandsnaam, grootte, afzender, bericht, verloopdatum en één
// Download-knop. Downloaden = een POST naar de engine (zodat een linkscanner in de mail niets telt),
// die een SAS-link van 15 minuten of een zip-link van 5 minuten teruggeeft; de opslag blijft privé.
// Verlopen, ingetrokken of onbekend: een nette melding. Niet indexeren (meta robots + X-Robots-Tag).
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { aantalTekst, grootteTekst, startDownload, zipKan, ZIP_TE_GROOT } from './bestandenApi';

const KLEUR = { tekst: '#1f2937', zacht: '#6b7280', lijn: '#eceef1', accent: '#526075', vlak: '#f8fafc' };
const KNOP = { textTransform: 'none', fontWeight: 600 };
const API = `${AI_ENGINE_URL}/api/v1/extern-bestand`;

const MELDINGEN = {
  onbekend: ['Deze link bestaat niet', 'Controleer of je de hele link uit de mail hebt gekopieerd.'],
  verlopen: ['Deze link is verlopen', 'Vraag de afzender bij Full Frame om een nieuwe link.'],
  ingetrokken: ['Deze link is ingetrokken', 'De afzender heeft deze link uitgezet. Neem contact op als je het bestand toch nodig hebt.'],
  te_vaak: ['Even te veel verzoeken', 'Probeer het over een minuut opnieuw.'],
  storing: ['Dit lukt nu even niet', 'Probeer het over een paar minuten opnieuw.'],
};

/** Antwoord → data, of een Error met .status (onbekend/verlopen/ingetrokken/te_vaak/storing). */
export async function leesAntwoord(r) {
  let body = null;
  try { body = await r.json(); } catch { body = null; }
  if (r.ok) return body;
  const d = body?.detail;
  const status = (d && typeof d === 'object' && d.status)
    || (r.status === 404 ? 'onbekend' : r.status === 410 ? 'verlopen' : r.status === 429 ? 'te_vaak' : 'storing');
  const fout = new Error((d && typeof d === 'object' && d.melding) || (typeof d === 'string' ? d : 'Mislukt'));
  fout.status = status;
  throw fout;
}

const vraag = (pad, methode = 'GET') => fetch(`${API}${pad}`, { method: methode, referrerPolicy: 'no-referrer' }).then(leesAntwoord);

/** "dinsdag 3 november" (NL-tijd). */
export function datumTekst(iso) {
  const d = iso ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('nl-NL', { timeZone: 'Europe/Amsterdam', weekday: 'long', day: 'numeric', month: 'long' }).format(d);
}

function useGeenIndex() {
  useEffect(() => {
    const extra = [['robots', 'noindex, nofollow'], ['referrer', 'no-referrer']].map(([name, content]) => {
      const m = document.createElement('meta');
      m.setAttribute('name', name);
      m.setAttribute('content', content);
      document.head.appendChild(m);
      return m;
    });
    const titel = document.title;
    document.title = 'Bestand van Full Frame';
    return () => { extra.forEach((m) => m.remove()); document.title = titel; };
  }, []);
}

function Kader({ children }) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: KLEUR.vlak, py: { xs: 4, md: 8 }, px: 2 }}>
      <Box sx={{ maxWidth: 560, mx: 'auto' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#94a3b8', mb: 2 }}>
          Full Frame
        </Typography>
        <Box sx={{ bgcolor: '#fff', border: `1px solid ${KLEUR.lijn}`, borderRadius: 2, p: { xs: 2.5, md: 4 } }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

export default function ExternDownload() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [fout, setFout] = useState(null);
  const [bezig, setBezig] = useState(null);   // null | 'alles' | item-id
  const [melding, setMelding] = useState('');
  useGeenIndex();

  useEffect(() => {
    let weg = false;
    vraag(`/${encodeURIComponent(token || '')}`)
      .then((d) => { if (!weg) setData(d); })
      .catch((e) => { if (!weg) setFout(e.status || 'storing'); });
    return () => { weg = true; };
  }, [token]);

  const download = async (wat, pad, zip = false) => {
    setBezig(wat);
    setMelding('');
    try {
      const d = await vraag(`/${encodeURIComponent(token)}${pad}`, 'POST');
      startDownload(zip ? `${AI_ENGINE_URL}${d.pad}` : d.url);
    } catch (e) {
      if (['verlopen', 'ingetrokken', 'onbekend'].includes(e.status)) setFout(e.status);
      else setMelding(e.message || 'Downloaden lukte niet; probeer het opnieuw.');
    } finally { setBezig(null); }
  };

  if (fout) {
    const [kop, tekst] = MELDINGEN[fout] || MELDINGEN.storing;
    return (
      <Kader>
        <Typography data-testid="melding" sx={{ fontSize: 20, fontWeight: 700, color: KLEUR.tekst, mb: 1 }}>{kop}</Typography>
        <Typography sx={{ color: KLEUR.zacht }}>{tekst}</Typography>
      </Kader>
    );
  }
  if (!data) return <Kader><Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box></Kader>;

  const omvang = data.is_map ? `${aantalTekst(data.aantal)} · ${grootteTekst(data.grootte)}` : grootteTekst(data.grootte);
  return (
    <Kader>
      <Typography sx={{ fontSize: 14, color: KLEUR.zacht, mb: 1.5 }}>
        {data.afzender_naam} van Full Frame stuurt je {data.is_map ? 'een map' : 'een bestand'}
      </Typography>
      <Typography component="h1" sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 700, color: KLEUR.tekst, wordBreak: 'break-word',
        display: 'flex', alignItems: 'center', gap: 1 }}>
        {data.is_map && <FolderOutlinedIcon sx={{ color: KLEUR.zacht }} />}
        <span>{data.naam}</span>
      </Typography>
      <Typography sx={{ fontSize: 14, color: KLEUR.zacht, mt: 0.5 }}>{omvang}</Typography>

      {data.notitie && (
        <Typography sx={{ mt: 2.5, pl: 1.5, borderLeft: `3px solid ${KLEUR.lijn}`, color: KLEUR.tekst, whiteSpace: 'pre-wrap', fontSize: 15 }}>
          {data.notitie}
        </Typography>
      )}

      {data.is_map && !zipKan(data, data.items) ? (
        <Typography data-testid="geen-zip" sx={{ fontSize: 14, color: KLEUR.zacht, mt: 3 }}>{ZIP_TE_GROOT} hieronder.</Typography>
      ) : (
        <Button variant="contained" disableElevation disabled={bezig !== null}
          onClick={() => (data.is_map ? download('alles', '/zip', true) : download('alles', '/download'))}
          sx={{ ...KNOP, mt: 3, bgcolor: KLEUR.accent, px: 3, '&:hover': { bgcolor: '#434f63' } }}>
          {bezig === 'alles' ? 'Bezig…' : data.is_map ? 'Alles downloaden (zip)' : 'Download'}
        </Button>
      )}
      {melding && <Typography role="alert" sx={{ fontSize: 13, color: '#b91c1c', mt: 1 }}>{melding}</Typography>}

      {data.is_map && data.items?.length > 0 && (
        <Box data-testid="items" sx={{ mt: 3, borderTop: `1px solid ${KLEUR.lijn}`, pt: 1.5 }}>
          {data.items.map((it) => (
            <Box key={it.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, fontSize: 14 }}>
              <Box component="span" sx={{ flex: 1, minWidth: 0, color: KLEUR.tekst, wordBreak: 'break-word' }}>{it.pad}</Box>
              <Box component="span" sx={{ color: KLEUR.zacht, fontSize: 12, flexShrink: 0 }}>{grootteTekst(it.grootte)}</Box>
              <Button size="small" disabled={bezig !== null} aria-label={`Download ${it.naam}`}
                onClick={() => download(it.id, `/items/${encodeURIComponent(it.id)}/download`)}
                sx={{ ...KNOP, color: KLEUR.accent, minWidth: 0, py: 0, flexShrink: 0 }}>
                {bezig === it.id ? 'Bezig…' : 'Download'}
              </Button>
            </Box>
          ))}
        </Box>
      )}

      <Typography sx={{ fontSize: 13, color: KLEUR.zacht, mt: 3 }}>
        De link werkt tot en met {datumTekst(data.verloopt_at)}. Vragen? Mail {data.afzender_naam} via{' '}
        <Box component="a" href={`mailto:${data.afzender}`} sx={{ color: KLEUR.accent }}>{data.afzender}</Box>.
      </Typography>
    </Kader>
  );
}
